import { Card, Conditional, Input, Select } from "@result/rui/components";
import { useForm, useNotifications } from "@result/rui/hooks";
import { ApiFilter } from "@result/rui/utils/api";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import * as yup from "yup";

import FormSubmitButtons from "#/components/FormSubmitButtons";
import NotAuthorized from "#/components/NotAuthorized";
import PageHeader from "#/components/PageHeader.jsx";
import SelectLookupTableItem from "#/components/SelectLookupTableItem";
import DataLayerSearchSelect from "#/components/data-layer/SearchSelect";
import * as DLD from "#/def/data-layer";
import * as LTD from "#/def/lookup-tables.ident.gen";
import useAuthorization from "#/hooks/useAuthorization";
import { useLookupTableList } from "#/hooks/useLookupTables";
import yesNoOptions from "#/model/YesNoOptions";
import { filterLastnostiPvBySpecies } from "#/pages/breeding-values/BreedingValueUtils";
import BreedingValuesMetodaIzracunaCheckbox from "#/pages/breeding-values/BreedingValuesMetodaIzracunaCheckbox";
import BreedingValuesPresets from "#/pages/breeding-values/BreedingValuesPresets";
import BreedingValuesTipPvCheckbox from "#/pages/breeding-values/BreedingValuesTipPvCheckbox";
import KodaIzracunSelect from "#/pages/breeding-values/KodaIzracunSelect";
import { useBreedingValuesBreedQuery } from "#/pages/breeding-values/useBreedingValuesBreedQuery";
import { downloadAsCSV } from "#/utils/ApiDownload";
import * as species from "#/utils/SpeciesScope";

const BreedingValuesDataDownloadPage = () => {
  const { t } = useTranslation();
  const supportedSpecies = species.getTranslated();
  const [isLoading, setIsLoading] = useState(false);
  const { addErrorNotification } = useNotifications();
  const authorization = useAuthorization();
  const auth = useAuth();

  const breedsQuery = useBreedingValuesBreedQuery();

  useEffect(() => {
    breedsQuery.query({});
  }, []);

  const filteredBreeds = useMemo(() => {
    return (breedsQuery.result?.list ?? []).filter((breed) =>
      supportedSpecies?.includes(breed.species)
    );
  }, [breedsQuery.result, supportedSpecies]);

  const downloadBreedingValues = async (values) => {
    const filter = new ApiFilter();
    filter.equal("kodaIzracun", values.kodaIzracun);
    if (values.kodaPopulacije != null && values.kodaPopulacije !== "") {
      filter.equal("kodaPopulacije", values.kodaPopulacije);
    }
    if (values.spolSifra != null && values.spolSifra !== "") {
      filter.equal("plemenskaZival.spol", values.spolSifra);
    }
    if (values.lastnost != null && values.lastnost !== "") {
      filter.equal("kodaPv", values.lastnost);
    }
    if (values.uradnost != null && values.uradnost !== "") {
      if (values.uradnost === "true") {
        filter.isTrue("uradnostVrednosti");
      } else {
        filter.isFalse("uradnostVrednosti");
      }
    }
    if (values.letoOd != null && values.letoOd !== "") {
      filter.greaterThenOrEqual("datumIzracuna", `${values.letoOd}-01-01`);
    }
    if (values.letoDo != null && values.letoDo !== "") {
      filter.lessThenOrEqual("datumIzracuna", `${values.letoDo}-12-31`);
    }
    if (values.pasma != null && values.pasma !== "") {
      filter.equal("plemenskaZival.pasmaSifra", values.pasma);
    }
    if (values.tipiPv?.length > 0) {
      filter.or((orGroup) => {
        values.tipiPv.map((tip) => {
          orGroup.equal("tipPlemenskeVrednosti", tip);
        });
      });
    }
    if (values.metodeIzracuna?.length > 0) {
      filter.or((orGroup) => {
        values.metodeIzracuna.map((metoda) => {
          orGroup.equal("metodaIzracuna", metoda);
        });
      });
    }

    filter.or((orGroup) => {
      supportedSpecies.map((species) => {
        orGroup.equal("plemenskaZival.species", species);
      });
    });

    try {
      setIsLoading(true);
      await downloadAsCSV({
        baseURL: "/api/plemenske_vrednosti",
        filter,
        token: auth.user?.access_token,
        zastopnistvoId: authorization.current,
      });
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.log("Error downloading breeding values", e);
      let errorMessage = null;
      try {
        const jsonString = await e.response.data.text();
        const json = JSON.parse(jsonString);
        errorMessage = json.details;
      } catch (parseErr) {
        console.error("Error parsing response", parseErr);
      }
      addErrorNotification(
        errorMessage ?? t("notification.downloadError"),
        t("notification.error")
      );
    }
  };

  const metodeIzracuna = useLookupTableList(LTD.SifPlemVredMetodaIzracuna);
  const tipiPv = useLookupTableList(LTD.SifPlemVredTipPv);

  const validationSchema = {
    kodaIzracun: yup.string().required(),
  };

  const initialValues = useMemo(() => {
    return {
      tipiPv: (tipiPv ?? []).map((tip) => tip.sifra),
      metodeIzracuna: (metodeIzracuna ?? []).map((metoda) => metoda.sifra),
    };
  }, [metodeIzracuna?.length, tipiPv?.length]);

  const { getField, submit, values, setValues } = useForm({
    initialValues: initialValues,
    schema: validationSchema,
    onSubmit: downloadBreedingValues,
  });

  useEffect(() => {
    if (metodeIzracuna != null && metodeIzracuna.length > 0) {
      getField("metodeIzracuna").setValue(metodeIzracuna.map((m) => m.sifra));
    }
  }, [metodeIzracuna?.length]);

  const officialityOptions = useMemo(() => yesNoOptions(t), [t]);

  const isAuthorized = authorization.isBF || authorization.isKIS;
  return (
    <>
      <Conditional condition={isAuthorized}>
        <form onSubmit={submit} className="breeding-values-page">
          <PageHeader
            title={t("breedingValues.dataDownload")}
            subtitle={t("breedingValues.dataDownload.description")}
            backURL=".."
          />

          <BreedingValuesPresets
            storageKey="BREEDING_VALUES_DOWNLOAD_PRESETS"
            values={values}
            onLoad={(newValues) => {
              setValues(newValues);
            }}
          />
          <Card>
            <div className="fixed-grid has-1-cols-mobile has-2-cols-tablet has-3-cols-desktop">
              <div className="grid">
                <KodaIzracunSelect
                  label={t("breedingValues.calculation")}
                  labelType="floating"
                  selectionMode="single"
                  formField={getField("kodaIzracun")}
                />
                <SelectLookupTableItem
                  ident={LTD.SifPlemVredKodaPopulacije}
                  label={t("breedingValues.populationCode")}
                  optionLabel={(kodaPopulacije) =>
                    kodaPopulacije.sifra + " - " + kodaPopulacije.naziv?.sl
                  }
                  labelType="floating"
                  formField={getField("kodaPopulacije")}
                />
                <SelectLookupTableItem
                  ident={LTD.SifSpol}
                  formField={getField("spolSifra")}
                  labelType="floating"
                />
                <Select
                  options={filteredBreeds}
                  label={t("animal.breed")}
                  formField={getField(`pasma`)}
                  optionId="sifra"
                  optionValue="sifra"
                  labelType="floating"
                  optionLabel={(breed) =>
                    [breed.sifra, breed.pasma?.naziv]
                      .filter(Boolean)
                      .join(" - ")
                  }
                />
                <DataLayerSearchSelect
                  ident={DLD.PLEMENSKA_VREDNOST_KODA}
                  queryFields={["sifra", "naziv.sl"]}
                  optionId="sifra"
                  optionLabel={(koda) =>
                    [koda.naziv?.sl, koda.sifra]
                      .filter(Boolean)
                      .join(" - ")
                      .toUpperCase()
                  }
                  filter={() => filterLastnostiPvBySpecies()}
                  optionValue="sifra"
                  label={t("breedingValues.property")}
                  formField={getField("lastnost")}
                  labelType="floating"
                />
                <Select
                  options={officialityOptions}
                  formField={getField("uradnost")}
                  optionId="id"
                  optionLabel="label"
                  optionValue="id"
                  label={t("breedingValues.officiality")}
                  labelType="floating"
                />
                <Input
                  label={t("breedingValues.yearFrom")}
                  formField={getField(`letoOd`)}
                  labelType="floating"
                />
                <Input
                  label={t("breedingValues.yearTo")}
                  formField={getField(`letoDo`)}
                  labelType="floating"
                />
              </div>
            </div>
            <BreedingValuesTipPvCheckbox formField={getField("tipiPv")} />

            <BreedingValuesMetodaIzracunaCheckbox
              formField={getField("metodeIzracuna")}
            />
            {/*  TODO izvor živali */}
          </Card>
          <FormSubmitButtons
            submitLabel={t("breedingValues.download")}
            onReset={() => setValues(initialValues)}
            isLoading={isLoading}
          />

          <div className="box has-background-light mt-4">
            <div className="is-flex is-size-5 is-gap-1">
              <span className="icon icon-circle-exclamation is-size-4"></span>
              <div>{t("breedingValues.upToResults", { value: 100000 })}</div>
            </div>
          </div>
        </form>
      </Conditional>
      <Conditional condition={!isAuthorized}>
        <NotAuthorized />
      </Conditional>
    </>
  );
};
export default BreedingValuesDataDownloadPage;
